import './styles/Box.scss';

import React            from 'react';
import AntdCard         from 'antd/lib/card';
import BoxContext       from '@widesk-ui/contexts/BoxContext';
import View             from '@widesk-ui/components/View';
import useDashboard     from '@widesk-ui/hooks/useDashboard';
import BoxStore         from '@widesk-ui/stores/BoxStore';
import useTheme         from '@widesk-ui/hooks/useTheme';
import { Action }       from '@widesk-ui/components/ButtonActions';
import ButtonActions    from '@widesk-ui/components/ButtonActions';
import { observer }     from 'mobx-react-lite';
import useDidMount      from '@widesk-core/hooks/useDidMount';
import { DownOutlined } from '@ant-design/icons';
import { UpOutlined }   from '@ant-design/icons';

export type BoxProps = {
	id?: string;
	className?: string;
	title?: React.ReactNode;
	cover?: React.ReactNode;
	children?: React.ReactNode;
	containerStyle?: React.CSSProperties;
	style?: React.CSSProperties;
	size?: 'default' | 'small';
	variant?: 'borderless' | 'outlined';
	inner?: boolean;
	bodyStyle?: React.CSSProperties;
	headStyle?: React.CSSProperties;
	renderTopLeft?: () => React.ReactNode;
	renderTopRight?: () => React.ReactNode;
	dashboardTitle?: string; // permet de saisir un titre précis pour le header du dashboard
	dashboardDisabled?: boolean; // permet de masquer l'ancre de la box dans le header du dashboard
	fullHeight?: boolean; // la box prend la hauteur de son conteneur (défaut "true" si on est dans un dashboard)
	actions?: Action[];
	collapse?: boolean | {
		default: boolean;
	},
}

const Box = observer((props: BoxProps) => {
	const [boxStore] = React.useState(() => new BoxStore(props));
	const [collapsed, setCollapsed] = React.useState(typeof props.collapse === 'object' ? props.collapse.default : false);

	const collapseEnabled = !!props.collapse;
	const isDisplayedExtra = !!props.renderTopRight || !!boxStore.ctxTopRight || !!props.actions;

	const theme = useTheme();
	const { dashboardStore } = useDashboard();

	boxStore.setProps(props);

	useDidMount(() => dashboardStore?.pushBoxStore(boxStore));

	const fullHeight = typeof props.fullHeight === 'undefined' ? !!dashboardStore : props.fullHeight;
	const title = collapseEnabled ? (
		<View centerV gapH="xxs" pointer onClick={() => setCollapsed(!collapsed)} row>
			<View size={13}>{collapsed ? <DownOutlined /> : <UpOutlined />}</View>
			{props.title}
		</View>
	) : props.title;

	return (
		<View
			className={[
				'widesk-box',
				props.className || '',
				fullHeight ? 'full-height' : '',
			]}
			id={boxStore.id}
			style={{ borderRadius: theme.borderRadius, ...props.containerStyle }}
		>
			<BoxContext.Provider value={{ boxStore }}>
				<AntdCard
					type={props.inner ? 'inner' : undefined}
					style={props.style}
					cover={props.cover}
					variant={props.variant}
					size={props.size}
					title={props.renderTopLeft ? (
						<View row gap={10} centerV>
							{props.renderTopLeft()}
							{title}
						</View>
					) : title}
					styles={{
						header: {
							color: theme.colorTextDescription,
							...props.headStyle,
						},
						body: {
							display: collapseEnabled && collapsed ? 'none' : undefined,
							...props.bodyStyle,
						},
						cover: {
							display: collapseEnabled && collapsed ? 'none' : undefined,
						},
					}}
					extra={isDisplayedExtra && (
						<View row gap={10} centerV>
							{!!props.renderTopRight && props.renderTopRight()}
							{!!boxStore.ctxTopRight && boxStore.ctxTopRight()}
							{!!props.actions && <ButtonActions actions={props.actions} />}
						</View>
					)}
				>
					{props.children}
				</AntdCard>
			</BoxContext.Provider>
		</View>
	);
});

export default Box;
